import { FieldProps, getIn, useFormikContext } from 'formik';
import * as React from 'react';
import { CloudinaryImage } from '../../../CloudinaryImage';
import { Dropzone } from '../Dropzone';

interface IImageProps extends FieldProps {
  accept?: string[];
  canRemove: boolean;
  maxSizeKB: number;
  previewMaxHeight: number;
  submitOnChange?: boolean;
  uploader: 'cloudinary' | 'activestorage';
}

export function Image({
  field: { name, onChange },
  form: { values },
  previewMaxHeight,
  submitOnChange,
  uploader,
  accept: initialAccept,
  canRemove,
  maxSizeKB,
}: IImageProps) {
  const { submitForm } = useFormikContext();

  const accept = initialAccept || ['png', 'jpeg'];
  const [src, setSrc] = React.useState<string>(getIn(values, name));
  const [imageId, setImageId] = React.useState<string>(null);

  function uploadCallback(file: File, newImageId: string) {
    if (uploader === 'activestorage') {
      setSrc(URL.createObjectURL(file));
    } else if (uploader === 'cloudinary') {
      setSrc(newImageId);
    }
    setImageId(newImageId);
    if (submitOnChange) {
      submitForm();
    }
  }

  React.useEffect(() => {
    if (imageId) {
      const input = document.createElement('input');
      input.name = name;
      input.value = imageId;
      input.onchange = onChange;
      input.dispatchEvent(new Event('change'));
    }
  }, [imageId]);

  function handleImageDelete() {
    setImageId('_destroy');
    setSrc(null);
  }

  const imageWrapStyles = {
    backgroundImage:
      'linear-gradient(45deg, #eee 25%, transparent 25%, transparent 75%, #eee 75%, #eee 100%),' +
      'linear-gradient(45deg, #eee 25%, white 25%, white 75%, #eee 75%, #eee 100%)',
    backgroundPosition: '0px 0px, 10px 10px',
    backgroundSize: '20px 20px',
    maxHeight: '200px',
  };

  function imageTag() {
    if (uploader === 'activestorage') {
      return <img src={src} alt={name} style={{ maxHeight: `${previewMaxHeight}px` }} />;
    }
    if (uploader === 'cloudinary') {
      return (
        <CloudinaryImage
          alt="Image preview"
          cloudName="pinpointhq"
          options={{ height: previewMaxHeight }}
          path={src}
          style={{ maxHeight: `${previewMaxHeight}px` }}
        />
      );
    }
  }

  const preview = (
    <>
      <div>
        <div style={imageWrapStyles}>{imageTag()}</div>
      </div>
      <button
        disabled={!canRemove}
        className="link link--error link--underline mar-t-1"
        onClick={handleImageDelete}
        type="button"
      >
        Remove image
      </button>
    </>
  );

  const upload = <Dropzone {...{ accept, uploadCallback, uploader }} maxSize={maxSizeKB * 1000} multiple={false} />;

  const uploadWithPreview = (
    <div className="frow frow--gutters">
      <div className="col-sm-1-2">{preview}</div>
      <div className="col-sm-1-2">{upload}</div>
    </div>
  );

  return src ? uploadWithPreview : upload;
}

Image.defaultProps = {
  canRemove: true,
  previewMaxHeight: 200,
};

export default Image;
